import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MainLayout from "../components/MainLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import Loader from "../components/ui/Loader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { apiGet } from "../lib/axiosInstance";

const TesterAppsPage = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [submissions, setSubmissions] = useState([]);
	const [pagination, setPagination] = useState({});

	const page = useMemo(() => Number(searchParams.get("page") || 1), [searchParams]);
	const limit = 12;

	const fetchSubmissions = async () => {
		setIsLoading(true);
		setError("");
		try {
			const response = await apiGet(`/tester-hub/submissions?page=${page}&limit=${limit}`);
			setSubmissions(Array.isArray(response?.submissions) ? response.submissions : []);
			setPagination(response?.pagination || {});
		} catch (err) {
			setError(err.message || "Failed to load testing apps");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchSubmissions();
	}, [page]);

	const setPage = (nextPage) => {
		const next = new URLSearchParams(searchParams);
		if (!nextPage || nextPage === 1) next.delete("page");
		else next.set("page", String(nextPage));
		setSearchParams(next);
	};

	return (
		<MainLayout>
			<main>
				<section className="rounded-2xl sm:rounded-3xl bg-card shadow-card border border-border p-4 sm:p-6 mb-6">
					<Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Tester Hub", to: "/tester-hub" }, { label: "Testing Apps" }]} />
					<div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
						<div>
							<h1 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-foreground">Available Testing Apps</h1>
							<p className="text-muted-foreground mt-1 sm:mt-2 max-w-2xl text-sm sm:text-base">
								Browse the latest builds submitted for testing.
							</p>
						</div>
						<Button asChild variant="secondary">
							<Link to="/tester-hub">Submit a build</Link>
						</Button>
					</div>
				</section>

				{isLoading ? (
					<Loader label="Loading testing apps" />
				) : error ? (
					<p className="text-red-500">{error}</p>
				) : submissions.length ? (
					<>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{submissions.map((s) => {
								const id = s._id || s.id;
								return (
									<Card key={id} className="h-full">
										<CardHeader>
											<CardTitle className="normal-case tracking-normal text-primary">{s.title}</CardTitle>
										</CardHeader>
										<CardContent>
											<p className="text-sm text-muted-foreground line-clamp-3">{s.description || "No description"}</p>
											<div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-2">
												<span>By {s.createdBy?.name || "Unknown"}</span>
												{s.apkUrl ? <span>• APK</span> : <span>• No APK</span>}
												<span>
													• {s.feedbackCounts?.working || 0} working / {s.feedbackCounts?.notWorking || 0} not working
												</span>
											</div>
										</CardContent>
										<CardFooter className="flex items-center justify-end">
											<Button asChild size="sm" variant="secondary">
												<Link to={`/tester-hub/${id}`}>Open</Link>
											</Button>
										</CardFooter>
									</Card>
								);
							})}
						</div>

						<div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-10">
							<Button size="sm" className="w-full sm:w-auto" disabled={page <= 1} onClick={() => setPage(page - 1)}>
								Previous
							</Button>
							<span className="text-sm text-muted-foreground">Page {page}</span>
							<Button
								size="sm"
								className="w-full sm:w-auto"
								disabled={!pagination?.hasNextPage}
								onClick={() => setPage(page + 1)}
							>
								Next
							</Button>
						</div>
					</>
				) : (
					<div className="text-center space-y-3 px-4">
						<p className="text-muted-foreground">No testing apps available yet.</p>
					</div>
				)}
			</main>
		</MainLayout>
	);
};

export default TesterAppsPage;
